/** @jsx jsx */

import React from "react";
import { jsx, css } from "@emotion/core";
import { Badge, Icon } from "antd";

import useRedirect from "../../helperHooks/useRedirect";

import { color, fonts } from "../../theme/Theme";
import { EligibilityViewConstants as CONSTANTS } from "./EligibilityViewConstants";

interface Props {
  /**
   *  indicates which TAB is being selected currently
   */
  currentIndex: number;
  /**
   *  indicates till which TAB, user has already completed
   */
  completedTill: number;
}

export enum StepStatus {
  success = "success",
  progress = "progress",
  pending = "pending"
}

/**
 *  Like a stepper, used to navigate b/w various TABS
 *  Can't go to TAB greater than "completedTill + 1"
 *  But can go to previous steps and modify.
 */
const FoldNavigation: React.FC<Props> = (props: Props) => {
  const { completedTill, currentIndex } = props;

  const redirect = useRedirect();

  const getTabClassName = (index: number) =>
    `step step-${
      currentIndex === index
        ? StepStatus.progress
        : completedTill > index
        ? StepStatus.success
        : StepStatus.pending
    } ${completedTill < index ? "step-inactive" : ""} my-2 my-lg-0`;

  const handleClick = (index: number, route: string) => {
    /**
     *  Preventing from calling handler if same TAB is clicked, or
     *  if index clicked is greater than completedTill
     */
    if (completedTill >= index && currentIndex !== index) {
      redirect(_ => route);
    }
  };
  const renderBadgeIcon = (index: number) =>
    index < completedTill ? (
      <Icon className="step-check-icon" type="check-circle" theme="filled" />
    ) : (
      undefined
    );

  return (
    <section css={foldNavigationStyles} className="pb-3">
      <ol className="d-flex align-items-center flex-row">
        {CONSTANTS.labels.eligibilitySteps.map((step, index) => (
          <React.Fragment key={index}>
            {index !== 0 && <Icon className="mx-3" type="right" />}
            <li
              className={getTabClassName(index)}
              onClick={() => handleClick(index, step.route)}
              onKeyDown={e =>
                e.key === "Enter" && handleClick(index, step.route)
              }
              tabIndex={0}
            >
              <Badge
                className="step-inner py-4 px-0"
                count={renderBadgeIcon(index)}
              >
                {step.stepTitle}
              </Badge>
            </li>
          </React.Fragment>
        ))}
      </ol>
    </section>
  );
};

export default FoldNavigation;

const foldNavigationStyles = css({
  ".step-check-icon": {
    color: color.darkGreen,
    backgroundColor: color.white
  },
  ".step": {
    flex: 1,
    textAlign: "center",
    borderRadius: "0.25rem",
    color: color.darkGreen,
    listStyle: "none",
    cursor: "pointer",
    "&:hover, &:active, &:focus": {
      backgroundColor: color.activeBackground
    },
    "&:focus": {
      outline: "none"
    }
  },
  ".step-inner": {
    width: "100%" //To align "check" icon
  },
  [`.step-${StepStatus.progress}`]: {
    border: `2px solid ${color.darkGreen}`,
    backgroundColor: color.activeBackground,
    color: color.primary,
    fontFamily: fonts.bold
  },
  [`.step-${StepStatus.success}`]: {
    border: `1px solid ${color.darkGreen}`,
    color: color.primary,
    fontFamily: fonts.bold
  },
  [`.step-${StepStatus.pending}`]: {
    border: `1px dashed ${color.border}`,
    color: color.secondary
  },
  ".step-inactive": {
    cursor: "not-allowed"
  }
});
